import { createContext, useContext, useEffect, useState } from "react";
import {
  onAuthStateChanged,
  signInWithEmailAndPassword,
  signOut,
} from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";
import { auth, db, SUPER_ADMIN_EMAIL } from "../firebase/config";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [role, setRole] = useState(null); // 'super' | 'admin' | 'editor' | null
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      setLoading(true);
      setUser(u);
      if (!u) {
        setIsAdmin(false);
        setIsSuperAdmin(false);
        setRole(null);
        setLoading(false);
        return;
      }
      const email = (u.email || "").toLowerCase();
      const superAdmin = email === SUPER_ADMIN_EMAIL.toLowerCase();
      setIsSuperAdmin(superAdmin);

      if (superAdmin) {
        setIsAdmin(true);
        setRole("super");
      } else {
        try {
          const snap = await getDoc(doc(db, "admins", email));
          if (snap.exists()) {
            setIsAdmin(true);
            setRole(snap.data().role || "editor"); // default editor ถ้าไม่มี field
          } else {
            setIsAdmin(false);
            setRole(null);
          }
        } catch {
          setIsAdmin(false);
          setRole(null);
        }
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  const login = (email, password) =>
    signInWithEmailAndPassword(auth, email, password);
  const logout = () => signOut(auth);

  // Derived permission helpers — ใช้ใน component ได้ง่าย
  const canEditSettings = role === "super" || role === "admin";
  const canEditContent = role != null; // super, admin, editor ทุกคนแก้ content ได้

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        isAdmin,
        isSuperAdmin,
        role,
        canEditSettings,
        canEditContent,
        login,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
